import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Send } from "lucide-react";
import AvgsFeedbackResults from "@/components/AvgsFeedbackResults";

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{value}</div>
    </div>
  );
}

export default function SurveyResultsPage() {
  const [data, setData] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [kind, setKind] = useState("wb");

  const load = async (preserveWeek) => {
    setLoading(true);
    try {
      const res = await api.get("/wb-surveys/results");
      setData(res.data);
      if (!preserveWeek && res.data.weeks.length) setSelectedWeek(res.data.weeks[0].week_start);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendNow = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/wb-surveys/send-now");
      if (res.created === 0) {
        toast.success("Alle Teilnehmer haben für diese Woche bereits eine Umfrage erhalten");
      } else if (res.sent < res.created) {
        toast.success(`${res.created} neue Umfragen erstellt, ${res.sent} E-Mails zugestellt (Rest ggf. Mail-Limit erreicht)`);
      } else {
        toast.success(`Umfrage an ${res.sent} Teilnehmer verschickt`);
      }
      load(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-8 text-xs text-slate-400">Lädt…</div>;

  const week = data?.weeks.find((w) => w.week_start === selectedWeek);

  const kindTabs = (
    <div className="flex gap-1.5 mb-8" data-testid="survey-kind-tabs">
      {[["wb", "Weiterbildung"], ["avgs", "AVGS-Coaching"]].map(([k, l]) => (
        <button key={k} onClick={() => setKind(k)} data-testid={`survey-kind-${k}`}
          className={`px-4 py-2 text-xs font-medium border ${kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
  );
  if (kind === "avgs") return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="survey-results-page">
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">AVGS-Coaching</div>
      <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Umfrage-Ergebnisse</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Feedbackbogen zum Offboarding — Erinnerungen nach 3 und 7 Tagen.</p>
      {kindTabs}
      <AvgsFeedbackResults />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="survey-results-page">
      {kindTabs}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">Weiterbildung</div>
          <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Umfrage-Ergebnisse</h1>
          <p className="text-sm text-slate-500 mt-1">
            Wöchentliche Unterrichtsbewertung — jeden Freitag 08:00 Uhr automatisch versendet, anonym ausgewertet.
          </p>
        </div>
        <button
          onClick={sendNow}
          disabled={sending}
          data-testid="survey-send-now-btn"
          className="px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5"
        >
          <Send size={12} strokeWidth={1.5} /> {sending ? "Sendet…" : "Jetzt manuell versenden"}
        </button>
      </div>

      {!data?.weeks?.length ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="survey-empty-state">
          Noch keine Umfrage-Ergebnisse. Die Umfrage wird automatisch jeden Freitag um 08:00 Uhr an alle
          Weiterbildungs-Teilnehmer einer laufenden Klasse versendet.
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="survey-week-tabs">
            {data.weeks.map((w) => (
              <button
                key={w.week_start}
                onClick={() => setSelectedWeek(w.week_start)}
                data-testid={`survey-week-tab-${w.week_start}`}
                className={`px-3 py-2 text-xs font-medium whitespace-nowrap border shrink-0 ${
                  selectedWeek === w.week_start
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {w.week_start}
              </button>
            ))}
          </div>

          {week && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <StatCard label="Antworten" value={`${week.total_submitted} / ${week.total_sent}`} testId="survey-stat-responses" />
                <StatCard label="Rücklaufquote" value={`${week.response_rate}%`} testId="survey-stat-rate" />
                <StatCard label="Gesamt-Ø" value={week.overall_avg != null ? week.overall_avg.toFixed(2) : "—"} testId="survey-stat-overall" />
              </div>
              <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="survey-question-results">
                {data.questions.map((q, idx) => {
                  const avg = week.avg_per_question[q.id];
                  if (q.type === "text") {
                    const texts = (week.text_answers || {})[q.id] || [];
                    return (
                      <div key={q.id} className="p-4" data-testid={`survey-result-${q.id}`}>
                        <div className="text-sm text-slate-800 leading-snug mb-2">{idx + 1}. {q.text}</div>
                        {texts.length === 0 ? <div className="text-xs text-slate-400">Noch keine Textantworten</div> : (
                          <ul className="space-y-1.5">
                            {texts.map((t, i) => <li key={i} className="text-xs text-slate-700 bg-slate-50 border-l-2 border-slate-300 px-3 py-2 whitespace-pre-wrap" data-testid={`survey-text-answer-${q.id}-${i}`}>{t}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={q.id} className="p-4 flex items-center justify-between gap-4" data-testid={`survey-result-${q.id}`}>
                      <div className="text-sm text-slate-800 flex-1 leading-snug">{idx + 1}. {q.text}</div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StarRating value={avg ? Math.round(avg) : 0} readOnly size={16} />
                        <span className="text-xs font-mono text-slate-500 w-10 text-right">{avg != null ? avg.toFixed(2) : "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
